import { z } from 'zod';
import { DEFAULT_PERSONA } from '../voice/catalog';

/**
 * The outreach campaign spec — one versioned document holding all three
 * parts of a campaign:
 *
 *   - **audience** — who qualifies (§N). A condition picked from a fixed
 *     catalog, plus its parameters. Not a query builder.
 *   - **sequence** — what we do when we reach them.
 *   - **success**  — what "worked" means for THIS campaign.
 *
 * The JSON is the runtime source of truth. The LLM compiler emits exactly
 * this shape at author time and is never consulted at runtime (ADR-154).
 *
 * Stored as `outreach_campaign_version.spec`. Versions are immutable and a
 * run pins the version it started on, so publishing can never change the
 * sequence under a patient who is already three steps in.
 */

// ───────────────────────────── Calling window ─────────────────────────────

/**
 * Day names match {@link OperatingHours} keys deliberately. The effective
 * window intersects with `locations.hours`, and a second naming convention
 * would mean a translation layer that can drift.
 */
export const WINDOW_DAYS = [
  'monday',
  'tuesday',
  'wednesday',
  'thursday',
  'friday',
  'saturday',
  'sunday',
] as const;
export type WindowDay = (typeof WINDOW_DAYS)[number];

/**
 * `HH:mm`, 24-hour, clinic-local.
 *
 * Exported so the compiler's edit schema constrains times the same way rather
 * than restating the regex. A second copy that drifted would let a model emit a
 * time this schema then rejects, failing the whole compile at the last step.
 */
export const timeOfDayString = z
  .string()
  .regex(/^([01]\d|2[0-3]):[0-5]\d$/, 'Expected HH:mm in 24-hour form');

export const outreachWindowSchema = z.object({
  days: z.array(z.enum(WINDOW_DAYS)).min(1),
  start: timeOfDayString,
  end: timeOfDayString,
  /** Also intersect with the location's operating hours. */
  useLocationHours: z.boolean().default(false),
});
export type OutreachWindow = z.infer<typeof outreachWindowSchema>;

// ─────────────────────────── Audience conditions ──────────────────────────

/**
 * The closed catalog. Adding a type is a code change — a `ConditionType`
 * implementation with a query builder — never configuration. That is what
 * keeps evaluation indexable enough to run on a schedule across the whole
 * patient population, and keeps arbitrary SQL out of a config table.
 */
export const AUDIENCE_CONDITION_TYPES = [
  'new_patients',
  'open_referral',
  'lapsed_patients',
  'upcoming_appointment',
  'post_visit_followup',
  'no_show_not_rebooked',
] as const;
export type AudienceConditionType = (typeof AUDIENCE_CONDITION_TYPES)[number];

/**
 * The dimensions a campaign may narrow its condition by.
 *
 * A closed list, not an open field registry. Each condition declares which of
 * these it can actually answer — a referral document carries no location, so
 * `open_referral` supports `provider` alone — and a filter it cannot answer is
 * rejected rather than silently ignored.
 */
export const AUDIENCE_FILTER_FIELDS = ['location', 'provider', 'appointmentType'] as const;
export type AudienceFilterField = (typeof AUDIENCE_FILTER_FIELDS)[number];

/**
 * One narrowing criterion: a dimension and the values that satisfy it.
 *
 * Values inside a criterion are alternatives — "Fremont or Oakland". Criteria
 * are combined with AND — "at Fremont or Oakland, AND with Dr Sahai". That is
 * the whole grammar, and it is deliberately not a tree.
 *
 * A previous cut allowed nested all/any groups and a negating operator. It
 * could express more, and an author reading it could no longer tell at a glance
 * who a campaign would call — which is the one thing this screen exists to make
 * obvious. Every real campaign shape wanted here is a flat AND of alternatives.
 * Nesting is a schema change if it is ever genuinely needed, and no published
 * version depends on it.
 */
export const audienceFilterCriterionSchema = z.object({
  field: z.enum(AUDIENCE_FILTER_FIELDS),
  // An empty criterion is not "match everything", it is an unfinished edit. It
  // would compile to `IN ()` and quietly match nobody.
  values: z.array(z.string().uuid()).min(1),
});
export type AudienceFilterCriterion = z.infer<typeof audienceFilterCriterionSchema>;

/**
 * Every criterion must hold. An empty list means no narrowing at all.
 *
 * The shape is bounded on purpose: each criterion compiles to `IN (…)` against
 * an indexed column and they are ANDed, so the reconciler's cost stays
 * predictable. It runs each active campaign's audience across the whole patient
 * population every fifteen minutes, and a predicate the planner cannot index
 * turns that into a sequential scan discovered in production.
 */
export const audienceFiltersSchema = z
  .array(audienceFilterCriterionSchema)
  // One criterion per dimension — two "Locations" rows would read as an
  // impossible AND, and the UI has no way to show which one lost.
  .refine((rows) => new Set(rows.map((r) => r.field)).size === rows.length, {
    message: 'Each dimension may only be narrowed once',
  })
  .default([]);
export type AudienceFilters = z.infer<typeof audienceFiltersSchema>;

/** Every dimension narrowed, for the "can this condition answer it?" check. */
export function collectFilterFields(filters: AudienceFilters): Set<AudienceFilterField> {
  return new Set(filters.map((c) => c.field));
}

/**
 * The single value the filters pin a dimension to, or null when they do not.
 *
 * The caller uses this to decide a run's LOCATION, which decides the timezone
 * its calling window is read in — so "probably Fremont" is not good enough, and
 * a wrong answer is how someone gets called at 6am. Several values are
 * alternatives, not a pin.
 */
export function pinnedFilterValue(
  filters: AudienceFilters,
  field: AudienceFilterField
): string | null {
  const criterion = filters.find((c) => c.field === field);
  return criterion?.values.length === 1 ? criterion.values[0]! : null;
}

const days = (max: number) => z.number().int().min(1).max(max);
const months = (max: number) => z.number().int().min(1).max(max);

/**
 * When a referral is complete enough to call (ADR-318, P5).
 *
 * NOT an audience parameter: the audience decides who QUALIFIES, and this
 * decides whether a qualifying referral can be called yet. A referral that
 * qualifies but is not ready gets no run at all, and its reason is computed when
 * the queue is read, which is what lets "what to do about not-ready" change
 * later without a data migration.
 *
 * A name and a dialable phone are always required and are not switches here: a
 * call without either cannot happen. The three switches are the ones a practice
 * may reasonably trade off.
 *
 * `dob` is a switch only for a campaign that never CALLS. A call verifies whoever
 * answers against the chart's date of birth and is never placed without one
 * (ADR-333), so a campaign with a voice step requires it whatever this says.
 */
export const referralReadinessSchema = z.object({
  /** A date of birth on the patient's chart. Always required when a step calls. */
  dob: z.boolean().default(true),
  /** At least one provider the referral could be booked with. */
  pool: z.boolean().default(true),
  /** Open slots with that pool within {@link slotHorizonDays}. */
  slots: z.boolean().default(true),
  slotHorizonDays: z.number().int().min(3).max(90).default(21),
});
export type ReferralReadiness = z.infer<typeof referralReadinessSchema>;

export const audienceSchema = z.discriminatedUnion('type', [
  z.object({
    type: z.literal('new_patients'),
    params: z.object({ registeredWithinDays: days(365) }),
    filters: audienceFiltersSchema,
  }),
  z.object({
    type: z.literal('open_referral'),
    params: z.object({
      /**
       * Zero is allowed, and means "as soon as it is ready": a complete referral
       * should not wait a day to be called just because it arrived today.
       */
      openForAtLeastDays: z.number().int().min(0).max(365),
      /**
       * Drop a referral once something has been booked against it.
       *
       * Defaults on, which suits a campaign converting referrals into
       * appointments — but that is one purpose. A campaign chasing the
       * paperwork on a referral, or checking benefits before the visit, wants
       * it open regardless of whether a date is on the books.
       *
       * "Booked" means an appointment because that is how a referral is
       * fulfilled in this domain today. A referral fulfilled some other way —
       * a prescription, a lab order — needs its own notion of fulfilment and
       * therefore its own condition; it must not be bolted onto this one.
       */
      excludeIfBooked: z.boolean().default(true),
    }),
    filters: audienceFiltersSchema,
    /** See {@link referralReadinessSchema}. Defaulted, so older versions parse. */
    // The field defaults have one home, `referralReadinessSchema`.
    readiness: referralReadinessSchema.default(() => referralReadinessSchema.parse({})),
  }),
  z.object({
    type: z.literal('lapsed_patients'),
    params: z.object({
      noVisitForMonths: months(60),
      /**
       * Keep patients who already have something on the books.
       *
       * Defaults to excluding them, which suits a campaign asking people to
       * come back — but that is the CAMPAIGN's purpose, not a fact about the
       * audience. A campaign with a different purpose (a benefits check, a
       * records request) may want everyone who has not been seen in N months
       * regardless of what is booked, and sets this true.
       */
      includeAlreadyBooked: z.boolean().default(false),
    }),
    filters: audienceFiltersSchema,
  }),
  z.object({
    type: z.literal('upcoming_appointment'),
    params: z.object({
      daysAhead: days(30),
      /**
       * Keep appointments the patient has already confirmed
       * (`APPOINTMENT_STATUS.ARRIVED`).
       *
       * Defaults to excluding them, which suits a campaign chasing
       * confirmations — but confirmation is one purpose among several. A
       * pre-visit intake, a "bring your insurance card", or a directions call
       * is just as relevant to a confirmed appointment, and sets this true.
       */
      includeConfirmed: z.boolean().default(false),
    }),
    filters: audienceFiltersSchema,
  }),
  z.object({
    type: z.literal('post_visit_followup'),
    params: z.object({ daysAfterVisit: days(90) }),
    filters: audienceFiltersSchema,
  }),
  z.object({
    type: z.literal('no_show_not_rebooked'),
    params: z.object({ withinDays: days(180) }),
    filters: audienceFiltersSchema,
  }),
]);
export type Audience = z.infer<typeof audienceSchema>;

/**
 * What a run can be *about* — the thing two runs must share before they
 * conflict. Which subject a given audience binds is declared by the condition
 * itself (`ConditionType.subjectKind`), never by a central table keyed on
 * condition type: the engine must be able to add an audience without anyone
 * editing a map of special cases.
 */
export const SUBJECT_KINDS = ['appointment', 'referral'] as const;
export type SubjectKind = (typeof SUBJECT_KINDS)[number];

/**
 * **Appointments are one class of subject, not the model.** The engine will run
 * prescription and lab outreach on the same machinery, and nothing outside a
 * condition may assume otherwise.
 *
 * Adding a class means: a value here, a `ConditionType` that declares it as its
 * `subjectKind`, and whatever success predicate reads that system of record.
 * It must NOT mean touching the reconciler, the run table's types, the conflict
 * query, or any table keyed on condition type — every one of those reads
 * `SubjectKind` rather than restating its members.
 */

// ───────────────────────────── Success ─────────────────────────────

/**
 * Success is a predicate over the evidence a run accumulated, declared per
 * campaign — not a fixed enum. An appointment *reminder* has no booking to
 * point at, and a reminder call where the patient confirms would otherwise
 * resolve as `engaged_no_booking`: a complete success recorded as a non-event.
 *
 * The two system-of-record conditions read a row, never the agent's claim.
 * A model saying "booked!" is not evidence.
 */
export type SuccessCondition =
  /** A new appointment exists for this patient, created after the run started. */
  | { kind: 'appointment_booked' }
  /** The run's target appointment reached `APPOINTMENT_STATUS.ARRIVED`. */
  | { kind: 'appointment_confirmed' }
  /** Any resolved attempt carried one of these dispositions. */
  | { kind: 'outcome'; anyOf: string[] }
  /** The agent captured every one of these fields. */
  | { kind: 'data_collected'; fields: string[] }
  | { kind: 'any'; of: SuccessCondition[] }
  | { kind: 'all'; of: SuccessCondition[] };

export const successConditionSchema: z.ZodType<SuccessCondition> = z.lazy(() =>
  z.discriminatedUnion('kind', [
    z.object({ kind: z.literal('appointment_booked') }),
    z.object({ kind: z.literal('appointment_confirmed') }),
    z.object({ kind: z.literal('outcome'), anyOf: z.array(z.string()).min(1) }),
    z.object({ kind: z.literal('data_collected'), fields: z.array(z.string()).min(1) }),
    z.object({ kind: z.literal('any'), of: z.array(successConditionSchema).min(1) }),
    z.object({ kind: z.literal('all'), of: z.array(successConditionSchema).min(1) }),
  ])
);

// ───────────────────────────── Sequence steps ─────────────────────────────

/** How we learn a step is finished. */
export const stepResolveSchema = z.discriminatedUnion('kind', [
  /** Voice: resolves the moment the interaction completes. */
  z.object({ kind: z.literal('on_call_end') }),
  /**
   * SMS: stays open. Resolves on booking / opt-out, or on silence — and the
   * clock RESETS on every inbound reply, so a live thread is never
   * interrupted by a call.
   */
  z.object({
    kind: z.literal('on_quiet'),
    quietForMinutes: z.number().int().positive(),
  }),
]);
export type StepResolve = z.infer<typeof stepResolveSchema>;

/**
 * What to do when AMD reports a machine. Per-step, not per-campaign: a message
 * on attempt one burns the only shot you get, while on the last attempt it is
 * the entire point.
 *
 * This is a *modifier on a call*, not an action — machine-vs-human is
 * orthogonal to what the step does, since a "voicemail step" still has to
 * handle a human picking up. Whether the message actually fired is recorded on
 * the attempt (`left_message`); the disposition stays `voicemail` either way,
 * so `OUTBOUND_CALL_OUTCOME` needs no new value.
 */
export const onMachineSchema = z
  .discriminatedUnion('kind', [
    z.object({ kind: z.literal('hang_up') }),
    z.object({ kind: z.literal('leave_message'), slot: z.string().min(1) }),
  ])
  .default({ kind: 'hang_up' });
export type OnMachine = z.infer<typeof onMachineSchema>;

/**
 * What starts a step.
 *
 * **Every step declares its own entry condition.** The delay between two steps
 * belongs to the step it starts, not to the one it follows — that is the only
 * attribution under which the question "when does this step begin?" has an
 * answer for every step in the sequence. Held the other way round (a
 * `waitAfterMinutes` on the preceding step) the last step owns a number that
 * can never elapse, which is a dead field the editor has to special-case away.
 *
 * It is a union rather than a bare number so the first step has a real value
 * instead of an ignored one, and so a later trigger kind — a fixed number of
 * days before an appointment, an event on the subject — is a new member here
 * rather than another migration.
 */
export const stepTriggerSchema = z.discriminatedUnion('kind', [
  /** The patient matched the audience. Only the first step may use this. */
  z.object({ kind: z.literal('qualifies') }),
  /** N minutes after the previous step resolved. Every other step uses this. */
  z.object({ kind: z.literal('after_previous'), minutes: z.number().int().min(0) }),
]);
export type StepTrigger = z.infer<typeof stepTriggerSchema>;

/** Minutes to wait before a step whose trigger is a delay. Zero otherwise. */
export function triggerDelayMinutes(trigger: StepTrigger): number {
  return trigger.kind === 'after_previous' ? trigger.minutes : 0;
}

/**
 * Which conversation a voice step runs (ADR-318, P1).
 *
 * The author picks a workflow; PUBLISHING the campaign pins that workflow's
 * active version here. A client always sends `versionId: null`, and the
 * publish route overwrites whatever it is sent. So a workflow republished later
 * never changes the call under a patient who is mid-sequence — only a campaign
 * republish moves the pin.
 */
export const stepWorkflowSchema = z.object({
  workflowDefnId: z.string().uuid(),
  versionId: z.string().uuid().nullable(),
});
export type StepWorkflow = z.infer<typeof stepWorkflowSchema>;

/**
 * What happens when a step has used every try and never reached them (ADR-318).
 *
 * Per STEP, where `continueOn` is per ATTEMPT. `next` is what every step did
 * before this field existed. On the last step it is ignored: there is nothing
 * to go on to, so the run ends either way.
 */
export const AFTER_LAST_TRY = ['next', 'stop'] as const;
export type AfterLastTry = (typeof AFTER_LAST_TRY)[number];

/**
 * The parts of the effective calling window a `defined` step may narrow to.
 * Morning runs from the window's start to 12:00, afternoon 12:00–15:00, late
 * afternoon 15:00 to the window's end; the resolver owns those bounds.
 */
export const TIMING_BANDS = ['morning', 'afternoon', 'late_afternoon', 'any'] as const;
export type TimingBand = (typeof TIMING_BANDS)[number];

/**
 * Clinic-local bounds of each band. `null` is the window's own edge that day,
 * which is what keeps a band RELATIVE: a later change to the campaign's hours
 * moves every morning step with it. Shared so the editor shows the hours a
 * band means with the same numbers the resolver narrows to.
 */
export const TIMING_BAND_BOUNDS: Record<TimingBand, { start: string | null; end: string | null }> =
  {
    morning: { start: null, end: '12:00' },
    afternoon: { start: '12:00', end: '15:00' },
    late_afternoon: { start: '15:00', end: null },
    any: { start: null, end: null },
  };

/**
 * A `defined` step's part of the day: one of the {@link TIMING_BANDS}, or
 * `custom` — exact clock times the author typed, held in `hours`.
 */
export const STEP_TIMING_BANDS = [...TIMING_BANDS, 'custom'] as const;
export type StepTimingBand = (typeof STEP_TIMING_BANDS)[number];

/** A clinic-local `HH:mm` stretch of one day. */
export interface ClockRange {
  start: string;
  end: string;
}

/**
 * One stretch of the day a `defined` step calls in (ADR-348): one of the
 * {@link TIMING_BANDS}, or `custom` — exact clock times the author typed, held
 * in `hours`.
 */
export const stepSlotSchema = z.object({
  band: z.enum(STEP_TIMING_BANDS),
  /**
   * Clinic-local `HH:mm` bounds, set exactly when `band` is `custom` (the
   * spec's refinement holds that). Defaulted so versions published before
   * custom hours existed still parse.
   */
  hours: z.object({ start: timeOfDayString, end: timeOfDayString }).nullable().default(null),
});
export type StepSlot = z.infer<typeof stepSlotSchema>;

/** How many slots a `defined` step may have. */
export const MAX_STEP_SLOTS = 4;

/**
 * The slots an `auto` step calls in (ADR-348): late morning and late afternoon
 * into the early evening, clinic-local, the hours outbound healthcare calling
 * most often reaches someone at home or on a break. Cut to the calling window
 * like any slot; a later version picks them per audience.
 */
export const AUTOMATIC_CALL_SLOTS: readonly ClockRange[] = [
  { start: '11:00', end: '13:00' },
  { start: '16:00', end: '19:00' },
];

const definedTimingSchema = z.object({
  mode: z.literal('defined'),
  slots: z.array(stepSlotSchema).min(1).max(MAX_STEP_SLOTS),
  /** Null means every day the campaign calls on. */
  days: z.array(z.enum(WINDOW_DAYS)).min(1).nullable(),
});

/**
 * A `defined` step as published before slots (ADR-348): one band, with its
 * own hours when the band is `custom`. Read as the one-slot step it is, so
 * those versions keep calling exactly when they did.
 */
const singleBandTimingSchema = z
  .object({
    mode: z.literal('defined'),
    band: z.enum(STEP_TIMING_BANDS),
    hours: z.object({ start: timeOfDayString, end: timeOfDayString }).nullable().default(null),
    days: z.array(z.enum(WINDOW_DAYS)).min(1).nullable(),
    // A spec with `slots` is the current shape: when those slots are invalid,
    // it must fail, not be re-read as this one with its slots dropped.
    slots: z.never().optional(),
  })
  .transform(({ band, hours, days }) => ({
    mode: 'defined' as const,
    slots: [{ band, hours }],
    days,
  }));

/**
 * When a step's calls go out (ADR-318, ADR-348): "When to call: Automatic /
 * Defined".
 *
 * Resolved AT RUNTIME against the campaign's effective window, never copied into
 * the step. An earlier draft had the editor write the campaign's hours into a
 * step `window`, which froze them: changing the campaign's hours later left
 * every step on the old ones.
 *
 * Both modes call in SLOTS: `auto` in {@link AUTOMATIC_CALL_SLOTS}, `defined`
 * in the author's, optionally on some of the campaign's days only. A slot can
 * only narrow: a day outside the campaign's days, or custom hours outside its
 * calling hours, are refused at publish. Which slot each try lands in is the
 * resolver's (`aimAttempt` in the API's `window.ts`).
 */
export const stepTimingSchema = z.union([
  z.object({ mode: z.literal('auto') }),
  definedTimingSchema,
  singleBandTimingSchema,
]);
export type StepTiming = z.infer<typeof stepTimingSchema>;

/**
 * The part of one open stretch a slot leaves: its band's bounds, or its custom
 * `hours`, cut to `open`. Null when it leaves nothing.
 *
 * Pure and shared so there is one answer to "when does this slot call": the
 * resolver applies it to each day's effective hours, and the editor to the
 * calling hours, to show a preset's hours and flag typed ones that miss.
 * Zero-padded `HH:mm` compares correctly as strings.
 */
export function slotHoursWithin(slot: StepSlot, open: ClockRange): ClockRange | null {
  const bounds =
    slot.band === 'custom' ? (slot.hours ?? TIMING_BAND_BOUNDS.any) : TIMING_BAND_BOUNDS[slot.band];
  const start = bounds.start && bounds.start > open.start ? bounds.start : open.start;
  const end = bounds.end && bounds.end < open.end ? bounds.end : open.end;
  return start < end ? { start, end } : null;
}

/** A slot's part of one open stretch, tagged with the slot's index in the step. */
export interface SlotStretch extends ClockRange {
  slot: number;
}

/**
 * Each slot cut to `open`, in order of start. Overlapping slots merge into one
 * stretch that keeps the lower index; slots that only touch (11:00–13:00 and
 * 13:00–15:00) stay apart, so a try can still move from one to the other.
 */
export function slotsWithin(slots: readonly StepSlot[], open: ClockRange): SlotStretch[] {
  const cut = slots.flatMap((slot, index) => {
    const hours = slotHoursWithin(slot, open);
    return hours ? [{ ...hours, slot: index }] : [];
  });
  cut.sort((a, b) => a.start.localeCompare(b.start) || a.slot - b.slot);
  const out: SlotStretch[] = [];
  for (const next of cut) {
    const last = out[out.length - 1];
    if (last && next.start < last.end) {
      if (next.end > last.end) last.end = next.end;
      last.slot = Math.min(last.slot, next.slot);
    } else {
      out.push(next);
    }
  }
  return out;
}

/** {@link AUTOMATIC_CALL_SLOTS} as slots, so both modes narrow one way. */
const AUTOMATIC_STEP_SLOTS: readonly StepSlot[] = AUTOMATIC_CALL_SLOTS.map((hours) => ({
  band: 'custom',
  hours,
}));

/**
 * The slots a step with `timing` calls in, and whether they may fall back to
 * the whole window when they leave nothing on any day: Automatic, and a
 * Defined step with only preset slots, may (a preset can miss a short window);
 * a custom slot never does (ADR-332): the author typed those hours.
 */
export function timingSlots(timing: StepTiming): {
  slots: readonly StepSlot[];
  mayFallBack: boolean;
} {
  if (timing.mode === 'auto') return { slots: AUTOMATIC_STEP_SLOTS, mayFallBack: true };
  return {
    slots: timing.slots,
    mayFallBack: !timing.slots.some((slot) => slot.band === 'custom'),
  };
}

/**
 * The hours a step calls in within one open stretch (the editor's calling
 * hours, a campaign's window): its slots cut to it, or the whole stretch when
 * they leave nothing and may fall back. The resolver applies the same two
 * pieces day by day, judging the fallback across the whole week.
 */
export function stepHoursWithin(
  timing: StepTiming,
  open: ClockRange
): { hours: ClockRange[]; fallsBack: boolean } {
  const { slots, mayFallBack } = timingSlots(timing);
  const cut = slotsWithin(slots, open).map(({ start, end }) => ({ start, end }));
  if (cut.length === 0 && mayFallBack) {
    return { hours: [{ start: open.start, end: open.end }], fallsBack: true };
  }
  return { hours: cut, fallsBack: false };
}

export const sequenceStepSchema = z.object({
  /** Stable across edits — attempts reference it, so it must not be regenerated. */
  id: z.string().min(1),
  label: z.string().max(80).optional(),
  channel: z.enum(['voice', 'sms']),
  action: z.discriminatedUnion('kind', [
    z.object({ kind: z.literal('call') }),
    z.object({ kind: z.literal('sms') }),
  ]),
  /** What starts this step. See {@link stepTriggerSchema}. */
  trigger: stepTriggerSchema,
  /** `{times:2, spacingMinutes:1440}` = "call twice, a day apart". */
  repeat: z
    .object({
      times: z.number().int().min(1).max(10).default(1),
      spacingMinutes: z.number().int().min(0).default(0),
    })
    .default({ times: 1, spacingMinutes: 0 }),
  resolve: stepResolveSchema,
  /** Outcomes that advance the sequence. An empty array makes the step terminal. */
  continueOn: z.array(z.string()),
  /** Narrows the campaign window. Can never widen it. */
  window: outreachWindowSchema.nullable().default(null),

  // ── Voice-only. Ignored on an SMS step. ──
  onMachine: onMachineSchema,
  /**
   * `rotate` places successive attempts in different thirds of the effective
   * window. Reach rate is dominated by whether you happen to call when someone
   * is free, and with a 1-day wait every attempt otherwise lands at the same
   * clock time.
   *
   * The rotation is DETERMINISTIC — band from the run's attempt ordinal, offset
   * from a stable hash of the run id. The simulator has to reproduce the driver
   * exactly, and the per-run jitter also spreads dispatches off the top of the
   * hour so a batch doesn't queue behind the concurrency cap.
   */
  timeOfDay: z.enum(['fixed', 'rotate']).default('fixed'),
  /**
   * Whether a patient-requested callback time overrides this step's own timing.
   *
   * Defaults to honouring it (ADR-318): a patient who asked for Thursday and got
   * the standard wait instead was called when they said they could not talk.
   * Versions published before the flip stored their value explicitly, so they
   * keep it.
   */
  onCallbackRequested: z
    .enum(['use_standard_wait', 'use_requested_time'])
    .default('use_requested_time'),

  // ── Added by ADR-318. All defaulted, so every earlier version parses. ──

  /** The workflow this voice step runs. Required for a voice step at publish. */
  workflow: stepWorkflowSchema.nullable().default(null),
  /**
   * The catalog agent (the stack: `voice/catalog.ts` `AGENTS`) that carries this
   * step's calls. Null means `DEFAULT_AGENT`. Shown to VagusX ops only; the API
   * does not hide it.
   */
  agentId: z.string().min(1).nullable().default(null),
  /** See {@link AFTER_LAST_TRY}. */
  afterLastTry: z.enum(AFTER_LAST_TRY).default('next'),
  /**
   * See {@link stepTimingSchema}.
   *
   * NULL MEANS "READ THE OLD FIELDS", which is why the default is null and not
   * `{mode: 'auto'}`. Versions published before this field existed have no
   * `timing` and do have `timeOfDay` + `window`; a non-null default would make
   * them all `auto` on the next parse and quietly move every one of their calls.
   * The editor writes `{mode: 'auto'}` for a new step.
   */
  timing: stepTimingSchema.nullable().default(null),
});
export type SequenceStep = z.infer<typeof sequenceStepSchema>;

// ───────────────────────────── The spec ─────────────────────────────

/**
 * Terminates the run from any step, regardless of that step's `continueOn`.
 * `engaged_no_booking` is included by default — one real conversation ends
 * outreach — and removing it from a campaign's `stopOn` IS the per-campaign
 * override.
 */
export const DEFAULT_STOP_ON = [
  'scheduled',
  'declined',
  'wrong_number',
  'engaged_no_booking',
  'escalated',
] as const;

export const campaignSpecSchema = z
  .object({
    version: z.literal(1),
    audience: audienceSchema,
    sequence: z.array(sequenceStepSchema).min(1).max(20),
    success: successConditionSchema,
    window: outreachWindowSchema,
    stopOn: z.array(z.string()).default([...DEFAULT_STOP_ON]),
    maxTotalAttempts: z.number().int().min(1).max(50).default(10),
    /** Hard TTL — a run older than this stops as `expired`. */
    expiresAfterDays: z.number().int().min(1).nullable().default(30),
    /**
     * WHO speaks on this campaign's calls: a catalog persona id
     * (`voice/catalog.ts` `PERSONAS`), checked against the catalog at publish
     * (ADR-318, P4). Per campaign, where the stack is per step, so a patient
     * hears the same voice on every call of a sequence whatever carries it.
     */
    personaId: z.string().min(1).default(DEFAULT_PERSONA.id),
  })
  /**
   * A trigger is only meaningful in one position. `qualifies` says "the patient
   * matched the audience", which happens once, at the start — on a later step it
   * would mean the run re-enters itself. `after_previous` on the FIRST step
   * names a previous step that does not exist.
   *
   * Enforced here rather than left to the editor, because the compiler and a
   * hand-written spec reach this schema too, and a spec that parses but cannot
   * run would fail invisibly inside a worker tick.
   */
  .superRefine((spec, ctx) => {
    spec.sequence.forEach((step, index) => {
      // Exact hours belong to `custom` and to nothing else: a band carrying
      // stale hours would leave two answers to "when does this slot call".
      const timing = step.timing;
      if (timing?.mode === 'defined') {
        timing.slots.forEach((slot, slotIndex) => {
          const path = ['sequence', index, 'timing', 'slots', slotIndex, 'hours'];
          if (slot.band === 'custom' && !slot.hours) {
            ctx.addIssue({ code: 'custom', path, message: 'Choose the hours this slot calls in' });
          } else if (slot.band !== 'custom' && slot.hours) {
            ctx.addIssue({ code: 'custom', path, message: 'Only custom timing has its own hours' });
          } else if (slot.hours && slot.hours.start >= slot.hours.end) {
            ctx.addIssue({ code: 'custom', path, message: 'The end time must be after the start' });
          }
        });
      }

      const wantQualifies = index === 0;
      if (wantQualifies && step.trigger.kind !== 'qualifies') {
        ctx.addIssue({
          code: 'custom',
          path: ['sequence', index, 'trigger'],
          message: 'The first step starts when the patient qualifies',
        });
      }
      if (!wantQualifies && step.trigger.kind !== 'after_previous') {
        ctx.addIssue({
          code: 'custom',
          path: ['sequence', index, 'trigger'],
          message: 'Every step after the first starts a set time after the one before it',
        });
      }
    });
  });
export type CampaignSpec = z.infer<typeof campaignSpecSchema>;

/** The channels this campaign's steps use. The one walk of the sequence for that question. */
export function campaignChannels(
  spec: Pick<CampaignSpec, 'sequence'>
): Set<SequenceStep['channel']> {
  return new Set(spec.sequence.map((step) => step.channel));
}

/** Does any step of this campaign place a call? */
export function campaignPlacesCalls(spec: Pick<CampaignSpec, 'sequence'>): boolean {
  return campaignChannels(spec).has('voice');
}

/**
 * A referral audience as it is actually CHECKED, which is not always as it was
 * authored.
 *
 * A call verifies whoever answers against the chart's date of birth and is never
 * placed without one (ADR-333), so a campaign that places calls requires a date
 * of birth whatever its `readiness.dob` switch says. One function, so the
 * reconciler, the audience preview and the description cannot disagree — a
 * preview that honoured the switch would promise calls the reconciler never
 * starts.
 *
 * ONLY A REFERRAL AUDIENCE HAS SWITCHES TO OVERRIDE. Every other audience comes
 * back unchanged, and the same rule reaches it another way: whoever counts or
 * starts its patients asks `campaignPlacesCalls` and drops those with no date
 * of birth (`OutreachMembershipService.selectReady`).
 */
export function audienceCheckedFor(audience: Audience, placesCalls: boolean): Audience {
  if (!placesCalls || audience.type !== 'open_referral' || audience.readiness.dob) return audience;
  return { ...audience, readiness: { ...audience.readiness, dob: true } };
}

/** {@link audienceCheckedFor}, for a campaign whose steps are known. */
export function checkedAudience(spec: Pick<CampaignSpec, 'audience' | 'sequence'>): Audience {
  return audienceCheckedFor(spec.audience, campaignPlacesCalls(spec));
}

// ─────────────────────── Patient-requested callback ───────────────────────

/**
 * A time the patient asked to be called back, captured by the voice agent and
 * returned in `structuredOutput`.
 *
 * Deliberately the same coarse shape as
 * `patient_contact_preferences.preferred_time_window`, so one resolver handles
 * both. Patients say "Thursday afternoon", not an instant.
 */
export const requestedCallbackSchema = z.object({
  /** ISO date (`2026-08-06`) when they named a day. */
  date: z.string().date().nullable(),
  timeOfDay: z.enum(['morning', 'afternoon', 'evening']).nullable(),
  notes: z.string().max(200).nullable(),
  /**
   * The exact clinic-local wall clock the patient agreed to, when the voice
   * agent got one. `null` when it degraded to the coarse pair above.
   *
   * ── WHY BOTH SHAPES, AND NOT A REPLACEMENT ─────────────────────────────────
   *
   * The coarse pair is still the answer for two live callers. Patient contact
   * PREFERENCES speak that vocabulary and always will — nobody sets a standing
   * preference for 14:30 — and `Callback` itself DEGRADES to it rather than
   * losing a confirmed phone number over a time it could not place. Replacing
   * them would delete the fallback and rewrite tests that are still correct.
   *
   * ── WHY WALL CLOCK AND NOT AN INSTANT ──────────────────────────────────────
   *
   * {@link resolveRequestedCallback} already takes the clinic timezone as a
   * REQUIRED argument and already resolves `date` + `timeOfDay` through it, so
   * an instant here would make this type carry two different kinds of time and
   * move resolution to the caller. It is also the value the patient actually
   * agreed to: the agent read "Tuesday, September 8 at 2:00 PM" back to them and
   * they said yes to that, not to a UTC offset computed from it.
   *
   * The trade, stated: if the clinic's configured timezone CHANGES between the
   * call and the callback, a wall clock re-resolves to a different instant where
   * a stored instant would not. That preserves the patient's intent (2pm local)
   * over the mistake, which is the right side to err on, and a location changing
   * zones mid-campaign is not a thing that quietly happens.
   */
  exactDate: z.string().date().nullable().optional(),
  /** 24-hour clinic-local `HH:MM`. Meaningless without `exactDate`. */
  exactTime: z
    .string()
    .regex(/^([01]\d|2[0-3]):[0-5]\d$/)
    .nullable()
    .optional(),
});
export type RequestedCallback = z.infer<typeof requestedCallbackSchema>;
